/**
 * Prepress home — the one prepress dashboard.
 * (docs/features/prepress-orchestration/17_safe_to_print_gate.md §"The
 * prepress surface".)
 *
 * Consolidates three former pages (PrepressHomePage, PrepressProblemQueuePage,
 * PrepressIntakeOrphansPage) into one, ordered by how much each section needs
 * a person right now:
 *
 *  - Safe to print   — the gate queue. Loudest, first.
 *  - Blocked         — failed runs, non-routine parked runs, and stray
 *                      prepress tickets. Absorbs the old problem queue's
 *                      "Needs attention" tab.
 *  - Orphans, Check warnings, Item proofs to approve, Tool failures — the
 *                      artwork family's lists (order to invoice doc 66 §4,
 *                      doc 83 §8): files nobody could place, a check we don't
 *                      know yet, a repeatable item's proof for staff to
 *                      decide, a check that could not run. Replace the old
 *                      artwork triage.
 *  - In flight       — ambient, ~everything else. Collapsed by default.
 *
 * This is a TEAM board, not a personal queue — no "your tickets"/"assigned to
 * you" framing anywhere (doc decision 6).
 *
 * Safe to print, Blocked, stray tickets and In flight all come from one
 * composed `getPrepressHome` call (`GET /views/prepress/home?site=`) — the
 * cross-schema join of prepress_pipeline_module's runs against
 * workflow_module's open prepress tickets the doc's "This forces a composed
 * view endpoint" calls for. The artwork family's lists come in the same
 * read (`home.artwork`), each step at the URL the list's row carries.
 *
 * The Routine sign-off tab from the old problem queue is gone, not ported:
 * `/resume` now refuses SAFE_TO_PRINT outright for both approve and reject —
 * that gate has its own dedicated routes and both live only in
 * PrepressGateDrawer below: sign-off via `recordSafeToPrint`
 * (`POST .../safe-to-print`), hold via `holdSafeToPrint`
 * (`POST .../hold`). ReviewActions / RejectDialog (the `/resume` call) are
 * for the Blocking-review group only — a different, non-gate stage.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { formatDistanceToNowStrict, format } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PrepressBlockedRun,
  PrepressGateJob,
  PrepressHome,
  PrepressInFlightJob,
  PrepressSite,
  Stage,
  TicketViewItem,
} from "@shared-core/data/api-types";
import {
  type PrepressArtworkCommand,
  type PrepressHeldFinding,
  type PrepressHomeArtwork,
  type PrepressItemProof,
  type PrepressOrphan,
} from "@shared-core/data/artwork-staff-types";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import {
  orderStepFailure,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { PREPRESS_SITE_LABELS } from "@shared-core/mock-data/prepress-home";
import { paths } from "@shared-core/routing/routes";
import {
  formatCount,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../app/components/primitives/toggle-group";
import { Badge } from "../app/components/primitives/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../app/components/primitives/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../app/components/primitives/collapsible";
import { Button } from "../app/components/primitives/button";
import { Label } from "../app/components/primitives/label";
import { Textarea } from "../app/components/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Ticket as TicketIcon,
} from "lucide-react";
import { PrepressGateDrawer } from "../app/components/domain/prepress/PrepressGateDrawer";
import { DecideProofDialog } from "../app/components/domain/artwork/ArtworkDialogs";
import {
  DecideHeldFindingDialog,
  DiscardOrphanDialog,
  ResolveOrphanDialog,
} from "../app/components/domain/prepress/PrepressArtworkDialogs";
import {
  PrepressBlockedDrawer,
  PREPRESS_STAGE_LABELS,
} from "../app/components/domain/prepress/PrepressBlockedDrawer";
import {
  PRIORITY_CONFIG,
  STATUS_CONFIG,
  type TicketPriority,
  type TicketStatusValue,
} from "../app/components/domain/tickets/TicketMetadataStrip";

export function PrepressHomePage() {
  const adapter = useDataAdapter();
  const [site, setSite] = useState<PrepressSite>("exeter");
  const [home, setHome] = useState<PrepressHome | null>(null);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeError, setHomeError] = useState<string | null>(null);
  const [inFlightOpen, setInFlightOpen] = useState(false);
  const [gateTarget, setGateTarget] = useState<PrepressGateJob | null>(null);
  const [blockedTarget, setBlockedTarget] = useState<PrepressBlockedRun | null>(
    null,
  );

  // The whole board — safe to print, blocked (ticket inlined), stray
  // tickets, and in-flight — comes from one composed, site-scoped call.
  const loadHome = useCallback(() => {
    let cancelled = false;
    setHomeLoading(true);
    setHomeError(null);
    adapter
      .getPrepressHome({ site })
      .then((res) => {
        if (!cancelled) setHome(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setHomeError(
            err instanceof Error
              ? err.message
              : "Failed to load the prepress board",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setHomeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, site]);

  useEffect(() => loadHome(), [loadHome]);

  function handleHeld(jobReference: string) {
    setGateTarget(null);
    toast.success(`${jobReference} put on hold`, {
      description: "Parked — there is no retry route from here yet.",
    });
    loadHome();
  }

  // A step on the artwork lists: post to the URL the list's row carries,
  // then reload the board; a refusal goes back to the dialog beside its input.
  const runArtwork = useCallback(
    async (
      command: PrepressArtworkCommand,
    ): Promise<OrderStepFailure | null> => {
      if (!home) return null;
      try {
        await adapter.applyPrepressArtworkCommand(home, command);
      } catch (err) {
        return orderStepFailure(err);
      }
      toast.success(
        command.kind === "resolve_orphan"
          ? "File placed"
          : command.kind === "discard_orphan"
            ? "File discarded"
            : command.kind === "decide_item_proof"
              ? "Proof decided"
              : "Check decided",
      );
      loadHome();
      return null;
    },
    [adapter, home, loadHome],
  );

  // An item proof's PDF: fetched with the caller's auth (never a plain anchor) and saved.
  const capabilities = usePlatformCapabilities();
  const viewProof = useCallback(
    async (url: string) => {
      try {
        const { blob, filename } = await adapter.downloadArtworkFamilyFile(url);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        toast.error(orderStepFailure(err).summary);
      }
    },
    [adapter, capabilities],
  );

  function handleSignedOff(jobReference: string) {
    setGateTarget(null);
    toast.success(`${jobReference} marked safe to print`);
    loadHome();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1>Prepress — {PREPRESS_SITE_LABELS[site]}</h1>
          <p className="text-sm text-muted-foreground">
            The shared prepress board for this site. Everyone on prepress sees
            the same rows here — nothing on this page is personalised.
          </p>
        </div>
        <ToggleGroup
          type="single"
          variant="outline"
          value={site}
          onValueChange={(v) => {
            if (v) setSite(v as PrepressSite);
          }}
        >
          <ToggleGroupItem value="exeter">Exeter</ToggleGroupItem>
          <ToggleGroupItem value="buckingham">Buckingham</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="space-y-6">
        <SafeToPrintSection
          jobs={home?.safe_to_print ?? []}
          loading={homeLoading}
          error={homeError}
          onSelect={setGateTarget}
        />
        <BlockedSection
          blocked={home?.blocked ?? []}
          loading={homeLoading}
          error={homeError}
          strayTickets={home?.open_tickets ?? []}
          onActed={loadHome}
          onViewDetail={setBlockedTarget}
        />
        <ArtworkListsSections
          lists={home ? home.artwork : null}
          loading={homeLoading}
          error={homeError}
          onCommand={runArtwork}
          onViewProof={viewProof}
        />
        <InFlightSection
          jobs={home?.in_flight ?? []}
          loading={homeLoading}
          error={homeError}
          open={inFlightOpen}
          onOpenChange={setInFlightOpen}
        />
      </div>

      <PrepressGateDrawer
        job={gateTarget}
        onOpenChange={(open) => {
          if (!open) setGateTarget(null);
        }}
        onHeld={handleHeld}
        onSignedOff={handleSignedOff}
      />
      <PrepressBlockedDrawer
        run={blockedTarget}
        onOpenChange={(open) => {
          if (!open) setBlockedTarget(null);
        }}
      />
    </div>
  );
}

function SectionHeader({
  title,
  count,
  hint,
}: {
  title: string;
  /** null while the count isn't known yet (loading) — renders "—". */
  count: number | null;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-sm font-semibold text-foreground">
        {title} · {count === null ? "—" : formatCount(count)}
      </h2>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

function SectionAlert({ title, message }: { title: string; message: string }) {
  return (
    <Alert variant="destructive">
      <AlertTriangle />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function LoadingLine({ message = "Loading…" }: { message?: string }) {
  return (
    <div className="flex items-center justify-center h-20">
      <div className="text-sm text-muted-foreground">{message}</div>
    </div>
  );
}

function EmptySection({ message }: { message: string }) {
  return (
    <div className="text-sm p-4 text-center border border-border rounded-lg text-muted-foreground">
      {message}
    </div>
  );
}

function SafeToPrintSection({
  jobs,
  loading,
  error,
  onSelect,
}: {
  jobs: PrepressGateJob[];
  loading: boolean;
  error: string | null;
  onSelect: (job: PrepressGateJob) => void;
}) {
  return (
    <div className="space-y-2">
      <SectionHeader
        title="Safe to print"
        count={loading ? null : jobs.length}
        hint="needs a person now"
      />
      {error ? (
        <SectionAlert
          title="Couldn't load the prepress board"
          message={error}
        />
      ) : loading ? (
        <LoadingLine />
      ) : jobs.length === 0 ? (
        <EmptySection message="Nothing is waiting at the gate." />
      ) : (
        <div className="space-y-1">
          {jobs.map((job) => {
            const hasWarning = job.evidence.some((e) => e.status === "warning");
            return (
              <button
                key={job.run_id}
                type="button"
                onClick={() => onSelect(job)}
                className="w-full text-left border border-border rounded-lg p-3 hover:bg-accent/50 transition-colors flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {job.job_reference}{" "}
                    <span className="font-normal text-muted-foreground">
                      {job.customer_name ?? "—"} · {job.product_name ?? "—"}
                    </span>
                    {job.hold && (
                      <Badge variant="neutral" className="ml-2 align-middle">
                        on hold
                      </Badge>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {PREPRESS_SITE_LABELS[job.site]} · rev {job.revision} ·{" "}
                    {/* "ripped" only holds for litho — digital, wide_format
                        and none presses don't go through a plate-imaging RIP
                        step (no rip evidence row at all, see the drawer), so
                        calling their gate-arrival time "ripped" would assert
                        a stage that never happened. Same field (`ready_at`),
                        method-branched label — do not collapse this back to
                        one word. Binary on purpose: every non-litho method
                        (digital, wide_format, none, or unresolved/null)
                        reads "ready" for the same reason, so there is no
                        need to enumerate them individually here. */}
                    {job.method === "litho" ? "ripped" : "ready"}{" "}
                    {format(new Date(job.ready_at), "HH:mm")}
                    {hasWarning && (
                      <Badge variant="warning" className="ml-2 align-middle">
                        route needs attention
                      </Badge>
                    )}
                  </p>
                  {job.hold && (
                    <p
                      className="text-xs text-muted-foreground line-clamp-1"
                      title={job.hold.reason}
                    >
                      {job.hold.operator} —{" "}
                      {formatDistanceToNowStrict(new Date(job.hold.held_at))}{" "}
                      ago: {job.hold.reason}
                    </p>
                  )}
                </div>
                <span className="text-sm text-primary shrink-0">Review ▸</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Blocked — absorbs the old problem queue's "Needs attention" tab (Failed +
 *  Blocking review) plus the stray-ticket list. All three groups come from
 *  the one composed `getPrepressHome` call now: `blocked` (partitioned
 *  client-side by `status`, not a cross-resource join — one collection,
 *  split for presentation) and `open_tickets`. */
function BlockedSection({
  blocked,
  loading,
  error,
  strayTickets,
  onActed,
  onViewDetail,
}: {
  blocked: PrepressBlockedRun[];
  loading: boolean;
  error: string | null;
  strayTickets: TicketViewItem[];
  onActed: () => void;
  onViewDetail: (run: PrepressBlockedRun) => void;
}) {
  const failed = blocked.filter((r) => r.status === "failed");
  const blockingReview = blocked.filter((r) => r.status !== "failed");
  const total = blocked.length + strayTickets.length;
  const [rejectTarget, setRejectTarget] = useState<PrepressBlockedRun | null>(
    null,
  );

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Blocked"
        count={loading ? null : total}
        hint="failed, parked, + stray tickets"
      />
      {error ? (
        <SectionAlert
          title="Couldn't load the prepress board"
          message={error}
        />
      ) : (
        <>
          <BlockedRunGroup
            title="Failed"
            description="Retry re-runs the failed stage; if it fails again, escalate to prepress engineering."
            items={failed}
            loading={loading}
            emptyMessage="Nothing has failed."
            renderActions={(row) => (
              <RetryAction row={row} onRetried={onActed} />
            )}
            onViewDetail={onViewDetail}
          />
          <BlockedRunGroup
            title="Blocking review"
            description="Parked for a decision outside the routine safe-to-print gate — e.g. a blocking preflight issue."
            items={blockingReview}
            loading={loading}
            emptyMessage="Nothing is blocking review."
            renderActions={(row) => (
              <ReviewActions
                row={row}
                onApproved={onActed}
                onRejectClick={() => setRejectTarget(row)}
              />
            )}
            onViewDetail={onViewDetail}
          />

          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Stray tickets · {loading ? "—" : formatCount(strayTickets.length)}
            </h3>
            {loading ? (
              <LoadingLine message="Loading tickets…" />
            ) : strayTickets.length === 0 ? (
              <EmptySection message="No prepress ticket without a linked run right now." />
            ) : (
              <div className="space-y-1">
                {strayTickets.map((t) => (
                  <StrayTicketRow key={t.id} ticket={t} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <RejectDialog
        row={rejectTarget}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        onRejected={() => {
          setRejectTarget(null);
          onActed();
        }}
      />
    </div>
  );
}

function BlockedRunGroup({
  title,
  description,
  items,
  loading,
  emptyMessage,
  renderActions,
  onViewDetail,
}: {
  title: string;
  description: string;
  items: PrepressBlockedRun[];
  loading: boolean;
  emptyMessage: string;
  /** Retry (Failed) or Approve/Reject (Blocking review) — each group wires
   *  its own row actions. */
  renderActions: (row: PrepressBlockedRun) => ReactNode;
  onViewDetail: (run: PrepressBlockedRun) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="space-y-0.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title} · {loading ? "—" : formatCount(items.length)}
        </h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      {loading ? (
        <LoadingLine />
      ) : items.length === 0 ? (
        <EmptySection message={emptyMessage} />
      ) : (
        <div className="space-y-1">
          {items.map((row) => (
            <div
              key={row.run_id}
              role="group"
              aria-label={row.job_reference}
              className="border border-border rounded-lg p-3 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  <Link
                    to={paths.jobWorkspace({ jobNo: row.job_reference })}
                    className="text-primary hover:underline"
                  >
                    {row.job_reference}
                  </Link>{" "}
                  <span className="font-normal text-muted-foreground">
                    {row.customer_id} ·{" "}
                    {PREPRESS_STAGE_LABELS[row.stage] ?? row.stage}
                  </span>
                  {row.ticket && (
                    <Badge variant="neutral" className="ml-2 align-middle">
                      <TicketIcon className="size-3" />
                      ticket
                    </Badge>
                  )}
                </p>
                <p
                  className="text-xs text-muted-foreground line-clamp-1"
                  title={row.message ?? ""}
                >
                  {row.message ?? "—"}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-3">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateTimeOrDash(row.recorded_at)}
                </span>
                {renderActions(row)}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onViewDetail(row)}
                >
                  Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewActions({
  row,
  onApproved,
  onRejectClick,
}: {
  row: PrepressBlockedRun;
  onApproved: () => void;
  onRejectClick: () => void;
}) {
  const adapter = useDataAdapter();
  const [approving, setApproving] = useState(false);

  async function handleApprove() {
    setApproving(true);
    try {
      const result = await adapter.resumeProblemRun(row.run_id, {
        stage: row.stage as Stage,
        approved: true,
      });
      toast.success(
        result.advanced && result.final_stage
          ? `${row.job_reference} approved — advanced to ${PREPRESS_STAGE_LABELS[result.final_stage] ?? result.final_stage}`
          : `${row.job_reference} approved`,
      );
      onApproved();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to approve ${row.job_reference}`,
      );
    } finally {
      setApproving(false);
    }
  }

  return (
    <div className="flex gap-2">
      <Button size="sm" onClick={handleApprove} disabled={approving}>
        {approving ? "Approving…" : "Approve"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onRejectClick}
        disabled={approving}
      >
        Reject
      </Button>
    </div>
  );
}

/** Retry a FAILED row (Blocked's Failed group). Backed by
 *  `POST /prepress/runs/{run_id}/retry`: re-runs the failed stage through the
 *  same execution path the original attempt used, then advances the run when
 *  the retry succeeds — see `retry_failed_stage`'s module docstring. A retry
 *  that fails again stays in Failed with a fresh message; the row's own
 *  "Retry" button is how an operator tries again. */
function RetryAction({
  row,
  onRetried,
}: {
  row: PrepressBlockedRun;
  onRetried: () => void;
}) {
  const adapter = useDataAdapter();
  const [retrying, setRetrying] = useState(false);

  async function handleRetry() {
    setRetrying(true);
    try {
      const result = await adapter.retryFailedStage(row.run_id, {
        stage: row.stage as Stage,
      });
      toast[result.status === "success" ? "success" : "error"](
        result.status === "success"
          ? result.advanced && result.final_stage
            ? `${row.job_reference} retried — advanced to ${PREPRESS_STAGE_LABELS[result.final_stage] ?? result.final_stage}`
            : `${row.job_reference} retried — succeeded`
          : `${row.job_reference} failed again — ${result.message ?? "see ticket"}`,
      );
      onRetried();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to retry ${row.job_reference}`,
      );
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Button size="sm" onClick={handleRetry} disabled={retrying}>
      {retrying ? "Retrying…" : "Retry"}
    </Button>
  );
}

function RejectDialog({
  row,
  onOpenChange,
  onRejected,
}: {
  row: PrepressBlockedRun | null;
  onOpenChange: (open: boolean) => void;
  onRejected: () => void;
}) {
  const adapter = useDataAdapter();
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setNotes("");
  }, [row?.run_id]);

  if (!row) return null;

  async function handleReject() {
    if (!row) return;
    setSubmitting(true);
    try {
      await adapter.resumeProblemRun(row.run_id, {
        stage: row.stage as Stage,
        approved: false,
        notes: notes.trim() || undefined,
      });
      toast.success(`${row.job_reference} rejected`);
      onRejected();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to reject ${row.job_reference}`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject {row.job_reference}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Parked at {PREPRESS_STAGE_LABELS[row.stage] ?? row.stage}. Rejecting
            records a failed decision on this stage — the work item stops here
            and is not resumable again through this queue.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="reject-notes">Reason (optional)</Label>
            <Textarea
              id="reject-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. spine width doesn't match the bound sample"
              rows={3}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleReject}
            disabled={submitting}
          >
            {submitting ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A prepress ticket with no live run behind it — has no job reference and
 *  opens no drawer (there is nothing to show), so it must not read like a
 *  blocked job that simply failed to load a title. Leads with the ticket
 *  affordance and carries a plain "no live run" chip, and links straight to
 *  the ticket rather than a drawer. */
function StrayTicketRow({ ticket }: { ticket: TicketViewItem }) {
  const status = ticket.status as TicketStatusValue;
  const priority = ticket.priority as TicketPriority;
  const claimed = !!ticket.assignee_user_name;
  return (
    <Link
      to={ticket._self}
      className="block border border-border rounded-lg p-3 hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm min-w-0 flex items-center gap-1.5">
          <TicketIcon className="size-3.5 text-muted-foreground shrink-0" />
          <span className="font-medium">"{ticket.title}"</span>
          <Badge variant="neutral" className="shrink-0">
            no live run
          </Badge>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {claimed ? `claimed by ${ticket.assignee_user_name}` : "unclaimed"}{" "}
            · {formatDistanceToNowStrict(new Date(ticket.created_at))}
          </span>
          <Badge variant={PRIORITY_CONFIG[priority].variant}>
            {PRIORITY_CONFIG[priority].label}
          </Badge>
          <Badge variant={STATUS_CONFIG[status].variant}>
            {STATUS_CONFIG[status].label}
          </Badge>
        </div>
      </div>
    </Link>
  );
}

/**
 * The artwork family's lists (docs/features/order-to-invoice/
 * 66_views_and_frontend_artwork_update_spec.md §4; 83_aw3c_item_artwork_plan.md
 * §8), in the order of how much each needs a person: orphans (files nobody
 * could place), check warnings (a check the classification does not know,
 * held for a person to tier; a repeatable item's own file links to the item),
 * item proofs to approve (a repeatable item's proof, which staff decide for
 * the customer) and tool failures (a check that could not run: its prepress
 * ticket is the team's, so there is nothing to act on here). They arrive in the composed home read
 * (`home.artwork`) with the URL each step posts to. Orphans have no site until
 * they are placed, so they show for every site.
 */
type ArtworkCommandRunner = (
  command: PrepressArtworkCommand,
) => Promise<OrderStepFailure | null>;

/** "2 days ago": a list row's age. */
function ageOf(at: string): string {
  return `${formatDistanceToNowStrict(new Date(at))} ago`;
}

/** Where a held or failed check's file lives: its order's or quote's page, or a repeatable item's (doc 83 §8). */
function SubjectLink({
  subject,
  orderNo,
  quoteNo,
  catalogueItemId,
}: {
  subject: string;
  orderNo: string | null;
  quoteNo: string | null;
  catalogueItemId: string | null;
}) {
  const to = orderNo
    ? paths.orderDetail({ orderNo, step: "artwork" })
    : quoteNo
      ? paths.quoteDetail({ quoteNo, step: "published" })
      : catalogueItemId
        ? paths.catalogueItem({ itemId: catalogueItemId })
        : null;
  return to ? (
    <Link to={to} className="underline underline-offset-2">
      {subject}
    </Link>
  ) : (
    <>{subject}</>
  );
}

function ArtworkListsSections({
  lists,
  loading,
  error,
  onCommand,
  onViewProof,
}: {
  lists: PrepressHomeArtwork | null;
  loading: boolean;
  error: string | null;
  onCommand: ArtworkCommandRunner;
  /** Fetch an item proof's PDF at the URL the row carries, and save it. */
  onViewProof: (url: string) => void;
}) {
  const [resolving, setResolving] = useState<PrepressOrphan | null>(null);
  const [discarding, setDiscarding] = useState<PrepressOrphan | null>(null);
  const [deciding, setDeciding] = useState<PrepressHeldFinding | null>(null);
  const [decidingProof, setDecidingProof] = useState<PrepressItemProof | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  // A board from a backend that does not carry the lists yet has none to draw.
  if (!loading && !error && !lists) return null;

  const run: ArtworkCommandRunner = async (command) => {
    setBusy(true);
    try {
      return await onCommand(command);
    } finally {
      setBusy(false);
    }
  };
  const state = (count: number | null) => ({
    count: loading ? null : count,
  });
  return (
    <>
      <div className="space-y-2">
        <SectionHeader
          title="Orphans"
          {...state(lists?.orphans.total ?? 0)}
          hint="arrived, couldn't be placed"
        />
        <p className="text-xs text-muted-foreground">
          An orphan has no site until it&apos;s placed on an order or quote:
          shown for every site.
        </p>
        {error ? (
          <SectionAlert title="Couldn't load the orphans" message={error} />
        ) : loading ? (
          <LoadingLine />
        ) : lists!.orphans.items.length === 0 ? (
          <EmptySection message="Every arrived file has been placed." />
        ) : (
          <div className="space-y-1">
            {lists!.orphans.items.map((orphan) => (
              <div
                key={orphan.id}
                role="group"
                aria-label={orphan.original_filename}
                className="border border-border rounded-lg p-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p
                    className="text-sm font-medium truncate"
                    title={orphan.original_filename}
                  >
                    {orphan.original_filename}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {orphan.source_label} · {ageOf(orphan.received_at)}
                    {orphan.reason_label && ` · ${orphan.reason_label}`}
                    {orphan.matched_number &&
                      ` · found with ${orphan.matched_number}`}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {orphan.resolve_url && orphan.resolve_targets.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Place ${orphan.original_filename}`}
                      onClick={() => setResolving(orphan)}
                    >
                      Place…
                    </Button>
                  )}
                  {orphan.discard_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Discard ${orphan.original_filename}`}
                      onClick={() => setDiscarding(orphan)}
                    >
                      Discard…
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {lists!.orphans.total > lists!.orphans.items.length && (
              <p className="text-xs text-muted-foreground">
                Showing the oldest {lists!.orphans.items.length} of{" "}
                {formatCount(lists!.orphans.total)}.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <SectionHeader
          title="Check warnings"
          {...state(lists?.check_warnings.total ?? 0)}
          hint="a check we don't know yet"
        />
        {error ? (
          <SectionAlert
            title="Couldn't load the check warnings"
            message={error}
          />
        ) : loading ? (
          <LoadingLine />
        ) : lists!.check_warnings.items.length === 0 ? (
          <EmptySection message="No check is waiting to be decided." />
        ) : (
          <div className="space-y-1">
            {lists!.check_warnings.items.map((finding) => (
              <div
                key={finding.id}
                role="group"
                aria-label={`${finding.check_name}: ${finding.subject}`}
                className="border border-border rounded-lg p-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{finding.check_name}</p>
                  <p className="text-xs text-muted-foreground">
                    <SubjectLink
                      subject={finding.subject}
                      orderNo={finding.order_no}
                      quoteNo={finding.quote_no}
                      catalogueItemId={finding.catalogue_item_id}
                    />
                    {finding.customer_name && ` · ${finding.customer_name}`}
                    {finding.detail && ` · ${finding.detail}`} · held{" "}
                    {ageOf(finding.held_since)}
                  </p>
                </div>
                {finding.decide_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`Decide ${finding.check_name} on ${finding.subject}`}
                    onClick={() => setDeciding(finding)}
                  >
                    Decide…
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <SectionHeader
          title="Item proofs to approve"
          {...state(lists?.item_proofs.total ?? 0)}
          hint="repeatable items' proofs: Pollards decides for the customer"
        />
        {error ? (
          <SectionAlert title="Couldn't load the item proofs" message={error} />
        ) : loading ? (
          <LoadingLine />
        ) : lists!.item_proofs.items.length === 0 ? (
          <EmptySection message="No item proof is waiting to be decided." />
        ) : (
          <div className="space-y-1">
            {lists!.item_proofs.items.map((row) => (
              <div
                key={row.id}
                role="group"
                aria-label={`${row.proof.label}: ${row.subject}`}
                className="border border-border rounded-lg p-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    <SubjectLink
                      subject={row.subject}
                      orderNo={null}
                      quoteNo={null}
                      catalogueItemId={row.catalogue_item_id}
                    />
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {row.proof.label} · of Rev {row.proof.of_revision}
                    {row.customer_name && ` · ${row.customer_name}`} · waiting{" "}
                    {ageOf(row.awaiting_since)}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`View ${row.proof.label} of ${row.subject}`}
                    onClick={() => onViewProof(row.proof.pdf_url)}
                  >
                    View {row.proof.label}
                  </Button>
                  {row.proof.decide_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Decide ${row.proof.label} of ${row.subject}`}
                      onClick={() => setDecidingProof(row)}
                    >
                      Decide…
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {lists!.item_proofs.total > lists!.item_proofs.items.length && (
              <p className="text-xs text-muted-foreground">
                Showing the oldest {lists!.item_proofs.items.length} of{" "}
                {formatCount(lists!.item_proofs.total)}.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <SectionHeader
          title="Tool failures"
          {...state(lists?.tool_failures.total ?? 0)}
          hint="the prepress ticket is the team's"
        />
        {error ? (
          <SectionAlert
            title="Couldn't load the tool failures"
            message={error}
          />
        ) : loading ? (
          <LoadingLine />
        ) : lists!.tool_failures.items.length === 0 ? (
          <EmptySection message="Every check has run." />
        ) : (
          <div className="space-y-1">
            {lists!.tool_failures.items.map((failure) => (
              <div
                key={failure.id}
                role="group"
                aria-label={failure.subject}
                className="border border-border rounded-lg p-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    <SubjectLink
                      subject={failure.subject}
                      orderNo={failure.order_no}
                      quoteNo={failure.quote_no}
                      catalogueItemId={failure.catalogue_item_id}
                    />
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {failure.reason_label}
                    {failure.customer_name &&
                      ` · ${failure.customer_name}`} ·{" "}
                    {ageOf(failure.failed_at)}
                  </p>
                </div>
                {failure.ticket_ref && (
                  <Badge variant="neutral" className="shrink-0">
                    <TicketIcon className="size-3" aria-hidden />
                    {failure.ticket_ref}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {resolving && (
        <ResolveOrphanDialog
          key={resolving.id}
          orphan={resolving}
          busy={busy}
          onClose={() => setResolving(null)}
          onSubmit={(input) =>
            run({ kind: "resolve_orphan", arrivalId: resolving.id, input })
          }
        />
      )}
      {discarding && (
        <DiscardOrphanDialog
          key={discarding.id}
          orphan={discarding}
          busy={busy}
          onClose={() => setDiscarding(null)}
          onSubmit={(input) =>
            run({ kind: "discard_orphan", arrivalId: discarding.id, input })
          }
        />
      )}
      {decidingProof && (
        <DecideProofDialog
          key={decidingProof.id}
          proof={decidingProof.proof}
          subject={decidingProof.subject}
          party={decidingProof.customer_name ?? "the customer"}
          busy={busy}
          onClose={() => setDecidingProof(null)}
          onDecide={(input) =>
            run({
              kind: "decide_item_proof",
              proofId: decidingProof.id,
              input,
            })
          }
        />
      )}
      {deciding && (
        <DecideHeldFindingDialog
          key={deciding.id}
          finding={deciding}
          busy={busy}
          onClose={() => setDeciding(null)}
          onSubmit={(input) =>
            run({ kind: "decide_finding", findingId: deciding.id, input })
          }
        />
      )}
    </>
  );
}

function InFlightSection({
  jobs,
  loading,
  error,
  open,
  onOpenChange,
}: {
  jobs: PrepressInFlightJob[];
  loading: boolean;
  error: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="space-y-2">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 w-full text-left"
        >
          {open ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
          <h2 className="text-sm font-semibold text-foreground">
            In flight · {loading ? "—" : formatCount(jobs.length)}
          </h2>
          <span className="text-xs text-muted-foreground ml-auto">
            ambient — nothing to action here
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1">
        {error ? (
          <SectionAlert title="Couldn't load in-flight work" message={error} />
        ) : loading ? (
          <LoadingLine />
        ) : jobs.length === 0 ? (
          <EmptySection message="Nothing in flight." />
        ) : (
          jobs.map((job) => (
            <div
              key={job.run_id}
              className="border border-border rounded-lg p-3 flex items-center justify-between gap-4 text-sm"
            >
              <p className="min-w-0">
                <span className="font-medium">{job.job_reference}</span>{" "}
                <span className="text-muted-foreground">
                  {job.customer_name ?? "—"}
                </span>
              </p>
              <span className="text-xs text-muted-foreground shrink-0">
                {job.stage_label}
              </span>
            </div>
          ))
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
